import React, { useState, useRef, useEffect } from "react";
import { ProductStatusExtDrawer } from "../../AssetLinking/StatusDrawers";
import systemListingService from "./SystemListingServices";

export default function ProductStatusExt(param: any) {


  const [ProductStatusExt, SetProductStatusExt] = useState<any>([]);
  useEffect(() => {
    if (param.activeTab === "Product Status Ext") {
      ProductStatusExtSelect()
    }
  }, [param]);

  const ProductStatusExtSelect = () => {
    var payload = {}
    systemListingService.statusProductExtList(payload).then((res:any) => { SetProductStatusExt(res) });
  }

  interface ProductStatusExtRow {
    id: number;              // internal row id
    parent: string;          // ProductStatusExtParent (kept in data, hidden in UI)
    useId: string;           // ProductStatusExtUseID
    shortCode: string;       // ProductStatusExtShortCode
    name: string;            // ProductStatusExtName (badge text)
    badgeVariant: "blue";
    isDefault: boolean;
    color: string;           // New color field
  }

  interface DrawerForm {
    useId: string;
    shortCode: string;
    name: string;
    isDefault: boolean;
  }

  const initialRows: ProductStatusExtRow[] = [
    {
      id: 1,
      parent: "1",
      useId: "1",
      shortCode: "ALL",
      name: "ALL",
      badgeVariant: "blue",
      isDefault: true,
      color: "#1e73d8", // default color
    },
    {
      id: 2,
      parent: "2",
      useId: "2",
      shortCode: "USD",
      name: "USD",
      badgeVariant: "blue",
      isDefault: false,
      color: "#1e73d8", // default color
    },
  ];

  const presetColors = [
    "#1e73d8",
    "#10b981",
    "#f59e0b",
    "#ef4444",
    "#6b7280",
    "#7c3aed",
    "#059669",
  ];

  const [rows, setRows] = useState<ProductStatusExtRow[]>(initialRows);
  const [editingOn, setEditingOn] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<"add" | "edit">("add");
  const [editingRowId, setEditingRowId] = useState<number | null>(null);

  const [drawerForm, setDrawerForm] = useState<DrawerForm>({
    useId: "",
    shortCode: "",
    name: "",
    isDefault: false,
  });

  const colorInputRefs = useRef<Map<number, HTMLInputElement | null>>(new Map());

  const recordCount = rows.length;

  // ===== Drawer helpers =====

  const updateDrawerField = <K extends keyof DrawerForm>(
    field: K,
    value: DrawerForm[K]
  ) => {
    setDrawerForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const toggleDrawerDefault = () => {
    setDrawerForm((prev) => ({
      ...prev,
      isDefault: !prev.isDefault,
    }));
  };

  const resetDrawer = () => {
    setDrawerForm({
      useId: "",
      shortCode: "",
      name: "",
      isDefault: false,
    });
  };

  const openAddDrawer = () => {
    if (!editingOn) return;
    setDrawerMode("add");
    setEditingRowId(null);
    resetDrawer();
    setDrawerOpen(true);
  };

  const openEditDrawer = (row: ProductStatusExtRow) => {
    if (!editingOn) return;
    setDrawerMode("edit");
    setEditingRowId(row.id);
    setDrawerForm({
      useId: row.useId,
      shortCode: row.shortCode,
      name: row.name,
      isDefault: row.isDefault,
    });
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
  };

  // ===== Row actions =====

  const handleMakeDefault = (rowId: number) => {
    if (!editingOn) return;
    setRows((prev) =>
      prev.map((row) => ({
        ...row,
        isDefault: row.id === rowId,
      }))
    );
  };

  const handleDeleteRow = (rowId: number) => {
    if (!editingOn) return;
    setRows((prev) =>
      prev
        .filter((row) => row.id !== rowId)
        .map((row, idx) => ({ ...row, id: idx + 1 }))
    );
  };

  const handleSaveDrawer = () => {
    const useId = drawerForm.useId.trim();
    const shortCode = drawerForm.shortCode.trim();
    const name = drawerForm.name.trim();
    const makeDefault = drawerForm.isDefault;

    if (!useId || !shortCode || !name) {
      alert("Please fill in all fields.");
      return;
    }

    if (drawerMode === "add") {
      setRows((prev) => {
        const nextId =
          prev.length > 0 ? Math.max(...prev.map((r) => r.id)) + 1 : 1;

        let updated: ProductStatusExtRow[] = [
          ...prev,
          {
            id: nextId,
            parent: "", // parent no longer set via UI
            useId,
            shortCode,
            name,
            badgeVariant: "blue", // all badges blue
            isDefault: makeDefault,
            color: "#1e73d8", // default color
          },
        ];

        if (makeDefault) {
          updated = updated.map((row) => ({
            ...row,
            isDefault: row.id === nextId,
          }));
        }

        return updated;
      });
    } else if (drawerMode === "edit" && editingRowId !== null) {
      setRows((prev) => {
        let updated = prev.map((row) =>
          row.id === editingRowId
            ? {
              ...row,
              useId,
              shortCode,
              name,
              isDefault: makeDefault,
            }
            : row
        );

        if (makeDefault) {
          updated = updated.map((row) => ({
            ...row,
            isDefault: row.id === editingRowId,
          }));
        }

        return updated;
      });
    }

    closeDrawer();
  };

  const handleRowColorChange = (rowId: number, color: string) => {
    setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, color: color } : r)));
  };

  const getContrastTextColor = (hex: string) => {
    try {
      const c = hex.replace("#", "");
      const r = parseInt(c.substring(0, 2), 16);
      const g = parseInt(c.substring(2, 4), 16);
      const b = parseInt(c.substring(4, 6), 16);
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      return brightness > 186 ? "#000000" : "#ffffff";
    } catch (e) {
      return "#ffffff";
    }
  };

  const openRowColorPicker = (rowId: number) => {
    const el = colorInputRefs.current.get(rowId);
    if (el) el.click();
  };

  // ===== Render =====

  return (
    <div className="content-card ws-12">
      {/* Edit tab */}
      <div className="ptl pbl ws-12">
        <span className="text-bold">Product Status Ext</span>
        <button className="icon-btn info pull-right" type="button" onClick={() => setEditingOn((prev) => !prev)}  >
          {editingOn ? "Edit Mode: On" : "Edit Mode: Off"}
        </button>

      </div>

      {/* Table */}
      <div className="table-wrapper">
        <table className={editingOn ? "editing-on newTable" : "newTable"} >
          <thead>
            <tr>
              <th className="col-useid" style={{ width: "15%" }}>Product Status Ext ID</th>
              <th style={{ width: "30%" }}>Name</th>
              <th className="col-short" style={{ width: "30%" }}>Short Code</th>
              <th style={{ width: "17%" }}>Operations</th>
            </tr>
          </thead>
          <tbody>
            {ProductStatusExt.map((row: any) => (
              <tr key={row.id}>
                <td >{row.productStatusExtUseID}</td>
                <td >{row.productStatusExtName} </td>
                <td >{row.productStatusExtShortCode}</td>

                <td style={{ pointerEvents: editingOn ? "auto" : "none", opacity: editingOn ? 1 : 0.6, cursor: editingOn ? "pointer" : "not-allowed", }}>

                  <button className={`btn-default ${row.isDefault ? "active" : ""}`} type="button" disabled={!editingOn} onClick={() => handleMakeDefault(row.id)}>
                    Default
                  </button>

                  <button className="icon-btn edit" type="button" disabled={!editingOn} onClick={() => openEditDrawer(row)}>
                    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 20h9" />
                      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
                    </svg>
                  </button>

                  <button className="icon-btn delete" type="button" disabled={!editingOn} onClick={() => handleDeleteRow(row.id)}>
                    <i className="icon-trash"> </i>
                  </button>
                </td>

              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add row button */}
      <div className="add-row-wrapper">
        <button
          id="addRowBtn"
          className="btn-add-row"
          type="button"
          onClick={openAddDrawer}
          disabled={!editingOn}
        >
          <svg
            width="19"
            height="19"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Add New Product Status Ext
        </button>

        <button
          id="tableSaveBtn"
          className="btn-save-table"
          type="button"
          disabled={!editingOn}
          onClick={() => {
            console.log("Saving table changes...", rows);
            setEditingOn(false);
          }}
        >
          Save Changes
        </button>
      </div>


      <ProductStatusExtDrawer
        open={drawerOpen}
        mode={drawerMode}
        form={drawerForm}
        onClose={closeDrawer}
        onReset={resetDrawer}
        onSave={handleSaveDrawer}
        updateField={updateDrawerField}
        onToggleDefault={toggleDrawerDefault}
      />

    </div>
  );
};

